import {
    IPanelProps,
    IRenderFunction,
    IStackProps, IStackTokens, Pivot,
    PivotItem,
    Separator,
    Stack,
    Text,
    Toggle, useTheme
} from '@fluentui/react';
import { RouterParams } from 'App';
import { RootState } from 'app/store';
import ControlWrapper, { EControlType } from 'components/ControlWrapper';
import { calcFieldName } from 'global/Application';
import { appInsights } from 'global/Application/AppInsight';
import { IFlexibleAttributePayload, changeField } from 'global/Reducer/dataSlice';
import { IVisibilityScope } from 'global/Visibility/IVisibilityScope.type';
import * as IXrmOptions from 'model/AttributeOptionSets.type';
import * as IXrmTypes from 'model/CRM';
import * as FAM from 'model/FlexibleAttributeModel';

import { ArrayToLookupByPredicateWithMap } from 'global/Utility/TSFunctions';
import { IFlexibleFormDefinition } from 'model/FlexibleFormModel';
import { useEffect, useMemo, useState } from 'react';
import { ConnectedProps, connect, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import NonStandardGridView from './NonStandardGridView';

interface NsField {
    [key: string]: boolean; // created for non standard template layer
}

const nsField: NsField = {
    "0973f3f2-d38d-ec11-b400-0022480a9a56": true,
    "ff72f3f2-d38d-ec11-b400-0022480a9a56": true,
    "fd72f3f2-d38d-ec11-b400-0022480a9a56": true
};

export enum EScopeType {
    benefit, plan, stopLoss, feesAndPremium, groupConfiguration, groupNumber, accountsReceivable, nonStandard, flexibleFieldSet, nonStandardTemplate, opportunityProductOptionBenefitFieldSet
}

interface IProps extends TComponentConnectedProps {
    fields: FAM.IFlexibleField[],
    category: IXrmTypes.bcbs_config_category;
    costshareCategory?: IXrmTypes.bcbs_config_category;
    tiers: FAM.ITierLevel[];
    disabled?: boolean;
    scope?: any;
    benefit?: FAM.IBenefit;
    groupConfiguration?: FAM.IGroupConfigurationData;
    cancellation?: boolean;
    firstPivotItem?: IRenderFunction<IPanelProps>;
    lastPivotItem?: IRenderFunction<IPanelProps>[];
    onChange?: (payload: IFlexibleAttributePayload) => void;
    bulk?: boolean;
    visibility?: IVisibilityScope;
    scopeType?: EScopeType;
    product?: boolean;
}

const FlexibleFieldView = (props: IProps) => {

    const tokens: IStackTokens = { childrenGap: '15px' }
    // const isNationalAlliance = props.state.data.json.lineOfBusiness?.value === CRMOptionSet.account.bcbs_lineofbusiness["National Alliance(808900001)"].value;
    // const hasRolePowerUser = authorizations?.roles["BCBS - Power User"] || authorizations?.roles['System Administrator'];

    const theme = useTheme();
    const [advancedMode, setAdvancedMode] = useState<Record<FAM.TGuid, boolean>>({}); //categoryid~true/false
    const dispatch = useDispatch();

    const params = useParams<RouterParams>()
    const history = useHistory();

    const tierNames = useMemo(() => {
        // lets create a distinct list of tiers:id with their corresponding name for a given benefit
        return props.tiers.reduce((prev, current) => {
            prev[current.id] = current;
            return prev;
        }, {} as Record<FAM.TGuid, FAM.ITierLevel>);

    }, [props.tiers]);

    const fieldIndexByConfigRef = useMemo(() => {
        const fieldList = ArrayToLookupByPredicateWithMap(
            props.fields,
            q => q.configRef.id,
            (item, index) => ({
                index: index!, item: item
            })
        );
        return fieldList;
    }, [props.fields?.length])

    const ignoreColumn = useMemo(() => {

        return props.scopeType === EScopeType.groupNumber ||
            props.scopeType === EScopeType.nonStandard ||
            props.scopeType === EScopeType.flexibleFieldSet ||
            props.scopeType === EScopeType.opportunityProductOptionBenefitFieldSet ||
            props.scopeType === EScopeType.nonStandardTemplate ||
            window.innerWidth < 720;

    }, [props.scopeType]);

    useEffect(() => {
        //TODO windows animation frame/or postLayoutEffect hook if there is one => useCallback on windowAnimation frame I think
        if (
            props.scopeType === EScopeType.benefit ||
            props.scopeType === EScopeType.plan ||
            props.scopeType === EScopeType.stopLoss ||
            props.scopeType === EScopeType.groupConfiguration ||
            props.scopeType === EScopeType.accountsReceivable ||
            props.scopeType === EScopeType.feesAndPremium
        ) {
            if (
                params.nonStandardId ||
                params.fieldId ||
                params.sectionId ||
                params.flexibleFieldSetId
            )
                if (history.location.search === '?navigation=pending')
                    setTimeout(() => {
                        let docElement: HTMLElement | null = null;

                        // TODO field id maybe better to be moved to FlexibleFieldView
                        // TODO move this to state management
                        if (params.fieldId) {
                            docElement = document.getElementById(params.fieldId);
                            history.push({ pathname: history.location.pathname, search: '?navigation=complete' })
                        } else if (params.nonStandardId) {
                            // TODO CustomDetailList has id.tolower()!
                            docElement = document.getElementById(`nonstandard.${params.sectionId}`);
                        }
                        else if (params.flexibleFieldSetId) {
                            // TODO CustomDetailList has id.tolower()!
                            docElement = document.getElementById(`flexiblefieldset(${params.sectionId})`);
                        } else if (params.sectionId) {
                            docElement = document.getElementById(params.sectionId);
                        }

                        docElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });

                    }, 0);

        }

        if (
            props.scopeType === EScopeType.flexibleFieldSet ||
            props.scopeType === EScopeType.nonStandard ||
            props.scopeType === EScopeType.nonStandardTemplate ||
            props.scopeType === EScopeType.groupNumber
        ) {
            if (history.location.search === '?navigation=pending')
                setTimeout(() => {
                    let docElement: HTMLElement | null = null;

                    if (params.field2ndId) {
                        docElement = document.getElementById(params.field2ndId);
                        history.push({ pathname: history.location.pathname, search: '?navigation=complete' })
                    } else if (params.section2ndId) {
                        docElement = document.getElementById(params.section2ndId);
                    }
                    docElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });

                }, 1000);
        }

    }, []);


    const fieldSetFactory = (
        field: FAM.IFlexibleField,
        configAttribute: IXrmTypes.bcbs_config_attribute,
        masterAttribute: IXrmTypes.bcbs_config_masterattribute,
    ): [JSX.Element[], EControlType] => {
        let disabled = props.disabled;

        const bcbs_attributetypecode = masterAttribute.bcbs_attributetypecode as EControlType;
        let controls: JSX.Element[] = [];

        if (!disabled)
            if (props.state.data.json.changeType) {

                switch (props.state.data.json.changeType?.value) {

                    case IXrmOptions.bcbs_groupconfigurationsnapshot.bcbs_changetypecode['Benefit Change(808900000)'].value:
                        disabled = disabled || (configAttribute.bcbs_isbenefitchange ?? false) === false;
                        break;

                    case IXrmOptions.bcbs_groupconfigurationsnapshot.bcbs_changetypecode['Benefit Renewal(808900010)'].value:
                        disabled = disabled || (configAttribute.bcbs_isbenefitchange ?? false) === false;
                        break;

                    case IXrmOptions.bcbs_groupconfigurationsnapshot.bcbs_changetypecode['PSA Change(808900012)'].value:
                        disabled = disabled || (configAttribute.bcbs_ispsachange ?? false) === false;
                        break;

                    case IXrmOptions.bcbs_groupconfigurationsnapshot.bcbs_changetypecode['Administrative Change(808900003)'].value:
                        disabled = disabled || (configAttribute.bcbs_isadministrativechange ?? false) === false;
                        break;

                    case IXrmOptions.bcbs_groupconfigurationsnapshot.bcbs_changetypecode['Financial Change(808900006)'].value:
                        disabled = disabled || (configAttribute.bcbs_isfinancialchange ?? false) === false;
                        break;

                    case IXrmOptions.bcbs_groupconfigurationsnapshot.bcbs_changetypecode['Financial Renewal(808900011)'].value:
                        disabled = disabled || (configAttribute.bcbs_isfinancialchange ?? false) === false;
                        break;

                    case IXrmOptions.bcbs_groupconfigurationsnapshot.bcbs_changetypecode['Stop Loss(808900009)'].value:
                        disabled = disabled || (configAttribute.bcbs_isstoplosschange ?? false) === false;
                        break;

                    case IXrmOptions.bcbs_groupconfigurationsnapshot.bcbs_changetypecode['Post Cancel Update(808900015)'].value:
                        disabled = disabled || (configAttribute.bcbs_ispostcancelupdate ?? false) === false;
                        break;

                    default:
                        break;
                }
            }

        if (bcbs_attributetypecode === EControlType.message)
            disabled = true;

        const bcbs_isreadonly = configAttribute.bcbs_isreadonly ?? false;

        if (bcbs_isreadonly)
            disabled = true;

        if (configAttribute.bcbs_islockedonproduct && props.product)
            disabled = true;

        // NA should start seeing all fields
        // if (!disabled) {
        //     // APP: Power Users should be able to edit Financial entities for NA configurations
        //     // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/258555
        //     if (isNationalAlliance)
        //         if (!configAttribute.bcbs_isbenefitchange)
        //             disabled = true;
        // }

        if (props.scopeType === EScopeType.nonStandard) {
            if (props.scope.partOfTemplate === true && nsField[field.configRef.id]) {
                disabled = true;
            }
        }


        if (props.state.status.debug.admin)
            disabled = false;


        if (field?.values.length > 0) {
            controls = field?.values?.map((fieldValue, fieldIndex) => {

                const id = fieldValue.tierRef?.id
                    ? `${fieldValue.tierRef?.id}_${fieldValue.id}`
                    : fieldValue.id

                const overrideValue: (FAM.IStringValue | null) = (bcbs_attributetypecode === EControlType.message)
                    ? {
                        type: "string",
                        value: props.state.data.config.cache.map_bcbs_config_attribute[field.configRef.id].bcbs_defaultvalue!
                    }
                    : null;


                //TODO: swap control types mapping of number to string back to number for control wrapper
                return (
                    <ControlWrapper
                        key={id}
                        id={id}
                        controlType={bcbs_attributetypecode as any}
                        label={calcFieldName(field, fieldValue, configAttribute, tierNames)}
                        disabled={disabled}
                        category={props.category}
                        costshareCategory={props.costshareCategory}
                        config={props.state.data.config}
                        locked={props.bulk}
                        field={field}
                        configAttribute={configAttribute}
                        masterAttribute={masterAttribute}
                        scope={props.scope}
                        state={props.state}
                        visibility={props.visibility}
                        defaultFieldValue={overrideValue ?? fieldValue.value}
                        fieldValueOnChange={(event, value) => {
                            // if no on change event is provided, we will dispatch to general store change
                            if (props.onChange) {
                                props.onChange({
                                    scope: props.scope,
                                    category: props.category,
                                    field: field,
                                    fieldValue: fieldValue,
                                    value: value
                                });
                            } else {
                                dispatch(changeField({
                                    scope: props.scope,
                                    category: props.category,
                                    field: field,
                                    fieldValue: fieldValue,
                                    value: value
                                }));
                            }
                        }}
                    />
                );
            })

        }

        return [controls, bcbs_attributetypecode]
    }

    const columnProps: Partial<IStackProps> = {
        tokens: { childrenGap: 15 },
        styles: {
            root: {
                // width: 600,
                // width:'98%',
                // minWidth:'97%',
                width: '100%',
                boxShadow: theme.effects.elevation8,
                padding: '10px 20px 20px 20px',
                margin: '20px 0px 0px 0px',
                backgroundColor: theme.semanticColors.menuBackground,
                borderRadius: '8px',
            },
        },
    };

    const pivotItems: JSX.Element[] = [];

    if (props.firstPivotItem) {
        const pivotItem = props.firstPivotItem();
        if (pivotItem)
            pivotItems.push(pivotItem);
    }

    const categoryId = props.category?.bcbs_config_categoryid;
    if (!categoryId)
        return <></>;

    let flexibleFormDefinition: IFlexibleFormDefinition = props.state.data.config.cache.categoryFormDefinitions[categoryId];

    if (props.scopeType === EScopeType.nonStandard) {
        const scopeTyped = props.scope as FAM.INonStandard;
        flexibleFormDefinition = props.state.data.config.cache.nonStandardFormDefinitions[props?.scope?.sectionRef?.id];
    }

    if (props.scopeType === EScopeType.flexibleFieldSet) {
        const scopeTyped = props.scope as FAM.IFlexibleFieldSet;
        flexibleFormDefinition = props.state.data.config.cache.attributeSetFormDefinitions[scopeTyped.attributeSetRef.id];
    }

    flexibleFormDefinition.tabs.forEach((tabDefinition, eachTabIndex) => {

        const eachTab = tabDefinition.tab;

        // only tabs that are associated with current config;
        if (eachTab._bcbs_config_categoryid_value !== props.category?.bcbs_config_categoryid)
            return;

        let pivotHasContent = false;

        if (eachTab.bcbs_isalwaysvisible)
            pivotHasContent = true;

        // if tab is marked for cancellation and we are not displaying cancellation
        if (eachTab.bcbs_iscancellation && props.cancellation !== true)
            return;

        // hide non-standards tab for flexible field set
        if (props.scopeType === EScopeType.flexibleFieldSet)
            if (eachTab.bcbs_name === "Non-Standards")
                return;

        const pivotItem = <PivotItem
            key={eachTab.bcbs_config_tabid}
            itemKey={eachTab.bcbs_config_tabid}
            headerText={eachTab.bcbs_name}
            style={{ marginBottom: '25px' }}
        >
            {tabDefinition.sections.map(sectionDef => {

                const eachSection = sectionDef.section;

                if (eachSection._bcbs_config_tabid_value !== eachTab.bcbs_config_tabid)
                    return;

                let sectionHasContent = false;

                if (eachSection.bcbs_isalwaysvisible)
                    sectionHasContent = true

                const configAttributes = props.state.data.config.cache.map_bcbs_config_attribute;
                const masterAttributes = props.state?.data.config.cache.map_bcbs_config_masterattribute;

                let hasAdvancedOption = false;

                let sectionElements: JSX.Element[] = [];

                sectionDef.rows.forEach(rowDef => {

                    const rowElements = [] as any[];
                    rowDef.attributes.forEach((attributeDefinition, index) => {

                        const eachAttributeDefinition = attributeDefinition.attribute;

                        const fieldIndex = fieldIndexByConfigRef[eachAttributeDefinition.bcbs_config_attributeid!]?.index;
                        if (fieldIndex == undefined)
                            return;

                        const eachField = props.fields[fieldIndex];

                        const configAttribute = configAttributes[eachField.configRef.id];

                        if (!configAttribute) {
                            console.warn("Missing attribute", eachField.configRef.id)
                            return;
                        }

                        const masterAttribute = masterAttributes[configAttribute._bcbs_config_masterattributeid_value!]!

                        const isHiddenReason = eachField.visibility
                        if (isHiddenReason) {
                            // TODO add a development build check to exclude from prod; performance gain...
                            if (!props.state.status.debug.diagnosis)
                                return;
                        }

                        // if does not belong to current section skip?
                        if (configAttribute._bcbs_config_sectionid_value !== eachSection.bcbs_config_sectionid)
                            return;

                        // is there any advanced option field?
                        if (configAttribute.bcbs_isadvancedoption)
                            hasAdvancedOption = true;

                        // are we displaying only MNILs
                        if (props.state.data.json.MNIL?.value && !configAttribute.bcbs_ismnil)
                            return;

                        // if we are not displaying advanced options & and field is an advanced field
                        if (configAttribute.bcbs_isadvancedoption && !advancedMode[eachSection.bcbs_config_sectionid!])
                            return;

                        if (configAttribute.statecode !== IXrmOptions.bcbs_config_attribute.statecode['Active(0)'].value)
                            return;

                        pivotHasContent = true;
                        sectionHasContent = true;

                        let [theFieldSet, theFieldsetType] = fieldSetFactory(eachField, configAttribute, masterAttribute);

                        theFieldSet.forEach((eachField, index) => {
                            if (ignoreColumn || theFieldsetType === EControlType.attributeSet)
                                sectionElements.push(eachField);
                            else
                                rowElements.push(
                                    <div
                                        key={eachField.key}
                                        style={{ width: '100%', minWidth: '0' }}
                                        id={eachField.props.configAttribute.bcbs_config_attributeid}
                                        data-field-config-id={eachField.props.configAttribute.bcbs_config_attributeid}
                                        data-field-config-name={eachField.props.configAttribute.bcbs_name}
                                    >
                                        {eachField}
                                    </div>
                                );
                        });

                    });

                    if (rowElements.length > 0) {

                        sectionElements.push(
                            <div
                                key={`row_${rowDef.row.bcbs_config_rowid}`}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: `repeat(${rowDef.row.bcbs_numberofcolumns}, 1fr)`,
                                    gap: '15px',
                                    alignItems: 'end',
                                }}
                            >
                                {rowElements}
                            </div>
                        )
                    }
                });


                if (eachSection.bcbs_isenablenonstandard) {
                    if (props.benefit || props.groupConfiguration) {

                        // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/245093
                        // for products we don't display nonstandards
                        if (!props.visibility?.product)
                            sectionElements.push(
                                <Stack.Item key={eachSection.bcbs_config_sectionid} >
                                    <NonStandardGridView
                                        key={eachSection.bcbs_config_sectionid}
                                        benefit={props.benefit}
                                        groupConfiguration={props.groupConfiguration}
                                        section={eachSection}
                                        disabled={props.disabled}
                                        tierNames={tierNames}
                                    />
                                </Stack.Item>
                            );

                        // sections with only non-standards should not display when only element
                        if (eachSection.bcbs_isalwaysvisible) {
                            pivotHasContent = true;
                            sectionHasContent = true;
                        }

                    }
                }

                // if section has no fields, lets skip rendering empty section
                if (!sectionHasContent)
                    return

                return <Stack
                    id={eachSection.bcbs_config_sectionid}
                    key={eachSection.bcbs_config_sectionid}
                    horizontal
                    horizontalAlign="space-between"
                >
                    <Stack.Item  {...columnProps}>
                        <Stack.Item>
                            <Stack
                                horizontal
                                horizontalAlign='space-between'
                                verticalAlign='baseline'
                            >
                                <Text
                                    variant='medium'
                                    styles={{
                                        root: {
                                            color: '#0078D7',
                                            textTransform: 'uppercase',
                                            fontWeight: '600',
                                            marginTop: '5px'
                                        }
                                    }}
                                >
                                    {eachSection.bcbs_name}
                                </Text>

                                {hasAdvancedOption &&
                                    <Toggle styles={{ root: { transform: 'scale(0.8, 0.8)' } }}
                                        inlineLabel
                                        label={'Show Advanced Options'}
                                        checked={advancedMode[eachSection.bcbs_config_sectionid!] ?? false}
                                        onChange={() => {
                                            const t = { ...advancedMode };
                                            t[eachSection.bcbs_config_sectionid!] = !t[eachSection.bcbs_config_sectionid!] ?? true;
                                            setAdvancedMode(t);
                                        }}
                                    />
                                }
                            </Stack>
                        </Stack.Item>
                        <Separator />
                        <Stack.Item>
                            <Stack tokens={{ childrenGap: '10px' }}>
                                {sectionElements}
                            </Stack>
                        </Stack.Item>
                    </Stack.Item>
                </Stack>
            })}
        </PivotItem>

        // if tab has no fields, lets skip rendering empty tab
        if (pivotHasContent)
            pivotItems.push(pivotItem);

        return;
    })

    props.lastPivotItem?.forEach((eachPivotItem, eachPivotItemIndex) => {
        pivotItems.push(eachPivotItem()!);
    })

    let _selectedTabKey: string | undefined = undefined
    if (
        props.scopeType === EScopeType.benefit ||
        props.scopeType === EScopeType.plan ||
        props.scopeType === EScopeType.stopLoss ||
        props.scopeType === EScopeType.groupConfiguration ||
        props.scopeType === EScopeType.accountsReceivable ||
        props.scopeType === EScopeType.feesAndPremium
    ) {
        _selectedTabKey = pivotItems.find(q => q.key === params.tabId)?.key as string;
    } else if (
        props.scopeType === EScopeType.nonStandard ||
        props.scopeType === EScopeType.flexibleFieldSet ||
        props.scopeType === EScopeType.groupNumber
    ) {
        _selectedTabKey = pivotItems.find(q => q.key === params.tab2ndId)?.key as string;
    }

    return <Pivot
        overflowBehavior='menu'
        defaultSelectedKey={_selectedTabKey}
        onLinkClick={(item, event) => {
            // if (
            //     props.scopeType === EScopeType.benefit ||
            //     props.scopeType === EScopeType.plan ||
            //     props.scopeType === EScopeType.stopLoss ||
            //     props.scopeType === EScopeType.groupConfiguration ||
            //     props.scopeType === EScopeType.accountsReceivable ||
            //     props.scopeType === EScopeType.feesAndPremium

            // ) {
            //     // Example : /editor/records/blueRelationship/939ab5f7-223f-eb11-a813-000d3a56bca1/groupConfiguration/9ae61b30-2aaf-ed11-aad1-000d3a985797/benefit/aa339e2e-677e-4cb6-8063-31251006d9c1/tab/a2df6811-0c43-ec11-8c60-000d3a5b8f01/section/dd85526f-7f75-ec11-8941-000d3a3527e6/nonStandard/c277299c-8d8f-4795-a3d2-2382002f6616
            //     // Result  : /editor/records/blueRelationship/939ab5f7-223f-eb11-a813-000d3a56bca1/groupConfiguration/9ae61b30-2aaf-ed11-aad1-000d3a985797/benefit/aa339e2e-677e-4cb6-8063-31251006d9c1/tab/a2df6811-0c43-ec11-8c60-000d3a5b8f01/section/dd85526f-7f75-ec11-8941-000d3a3527e6/nonStandard/c277299c-8d8f-4795-a3d2-2382002f6616

            //     // lets grab everything before first tab encounter
            //     // const basePath = history.location.pathname.match(/.*?\/(benefit|plan|stopLoss|root|claimsAR|feesAndPremium)\/.{36}\/tab/ig)?.[0];

            //     // if (basePath)
            //     //     history.push(basePath + `/${item?.props.itemKey}`);
            // }

            appInsights?.trackPageView({
                name: `Pivot | ${item?.props?.headerText}`,
                pageType: "pivot"
            });

        }}
    >
        {pivotItems}
    </Pivot>;
}

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(FlexibleFieldView);